import { PersonaFieldTypes } from '@personas/enums/persona-fields-types.enum'

/**
 * Channels for resend confirmation (database bulk / single flows).
 */
export enum NotificationChannel {
  EMAIL = 'email',
  SMS = 'sms',
  WHATSAPP = 'whatsapp'
}

/**
 * Whether the persona has a phone-number field (form_element_type_id = PersonaFieldTypes.PHONE).
 * SMS / WhatsApp require this field; Email does not.
 * @param form Persona (or form-shaped object) whose form_elements should be inspected
 * @returns true when at least one element is a phone number
 */
export function formHasPhoneNumberElement(form?: { form_elements?: { form_element_type_id?: number }[] } | null): boolean {
  return !!form?.form_elements?.some((element) => element.form_element_type_id === PersonaFieldTypes.PHONE)
}

/**
 * Drops SMS and WhatsApp when the form has no phone-number element.
 * @param channels Candidate channels (order preserved)
 * @param form Persona whose form_elements decide phone-channel visibility
 * @returns Channels that can be offered for this form
 */
export function notificationChannelsForForm(
  channels: NotificationChannel[],
  form?: { form_elements?: { form_element_type_id?: number }[] } | null
): NotificationChannel[] {
  if (formHasPhoneNumberElement(form)) return channels
  return channels.filter((ch) => ch !== NotificationChannel.SMS && ch !== NotificationChannel.WHATSAPP)
}

/**
 * Whether the picker would show only Email (channel step can be skipped).
 * @param channels Channels after form-based filtering
 * @returns true when Email is the sole option
 */
export function isEmailOnlyNotificationChannel(channels: NotificationChannel[]): boolean {
  return channels.length === 1 && channels[0] === NotificationChannel.EMAIL
}

/** Short UI label for Email / SMS / WhatsApp (invite, resend, channel picker). */
export function notificationChannelDisplayName(ch: NotificationChannel): string {
  switch (ch) {
    case NotificationChannel.EMAIL:
      return 'email'
    case NotificationChannel.SMS:
      return 'SMS'
    case NotificationChannel.WHATSAPP:
      return 'WhatsApp'
  }
}

/** Single-row flow: same channels as bulk (`resend-mail` / `resend-sms` / `resend-wa` on registrations API). */
export function resendConfirmationChannelsForSingle(): NotificationChannel[] {
  return [NotificationChannel.EMAIL, NotificationChannel.WHATSAPP, NotificationChannel.SMS]
}

/** Bulk flow: all three channels. */
export function resendConfirmationChannelsForBulk(): NotificationChannel[] {
  return [NotificationChannel.EMAIL, NotificationChannel.WHATSAPP, NotificationChannel.SMS]
}

/** Success toast detail after single-row resend (flow UI + `Registration.resendConfirmation`). */
export function resendConfirmationSuccessToastBody(channel: NotificationChannel): string {
  switch (channel) {
    case NotificationChannel.SMS:
      return 'Confirmation SMS has been queued for this registrant.'
    case NotificationChannel.WHATSAPP:
      return 'Confirmation WhatsApp message has been queued for this registrant.'
    default:
      return 'Confirmation email sent to the registrant’s email address.'
  }
}
